import { Injectable } from '@angular/core';
import { DecsService } from '@shared/services/decs/decs.service';
import { DateTime } from 'luxon';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { FlightList, FlightListItem } from './models/flight-list';
import { RequestState } from '@shared/models/request-state';
import { SequenceRequest } from '@sequence/models/request/sequence.request';
import { CrewService } from '@flight-details/crew/crew.service';
import { CrewRequest } from '@flight-details/crew/models/request/crew.request';
import { SavedFlight } from './models/saved-flight';
import { stripTime } from '@shared/utilities/utilites';
import { AlertService } from '@shared/services/alert/alert.service';

@Injectable({
  providedIn: 'root',
})
export class FlightSearchStore {
  // Column indices [start, end] for parsing flights list.
  private readonly FLIGHT_COL = [0, 4];
  private readonly DEP_TIME_COL = [6, 10];
  private readonly STATUS_COL = [10, 11];
  private readonly ARR_TIME_COL = [18, 22];
  private readonly EQUIP_COL = [34, 37];
  private readonly CXLD_COL = [13, 17];

  private readonly _savedFlights = new BehaviorSubject<SavedFlight[]>(
    this.getSavedFlightsFromLocalStorage()
  );
  savedFlights$ = this._savedFlights.asObservable();

  constructor(
    private decsService: DecsService,
    private crewService: CrewService,
    private alertService: AlertService
  ) {
    this.getSavedFlightsFromLocalStorage();
  }

  private getSavedFlightsFromLocalStorage(): SavedFlight[] {
    const savedFlights = localStorage.getItem('savedFlights');

    if (savedFlights) {
      return JSON.parse(savedFlights) as SavedFlight[];
    }

    return [];
  }

  private setSavedFlights(savedFlights: SavedFlight[]): void {
    console.log('saving saved flights');
    localStorage.setItem('savedFlights', JSON.stringify(savedFlights));
    this._savedFlights.next(savedFlights);
  }

  /** Removes any pinned flights that departed more than one day ago. */
  removeExpiredFlights(): void {
    const savedFlights = [...this._savedFlights.getValue()];
    const clearedSavedFlights = savedFlights?.filter((flight) => {
      const yesterday = DateTime.now().minus({ day: 1 }).startOf('day');
      return DateTime.fromISO(flight.date).startOf('day') >= yesterday;
    });

    // Only update if needed.
    if (savedFlights.length !== clearedSavedFlights.length) {
      this.setSavedFlights(clearedSavedFlights);
    }
  }

  /** Adds/removes saved flight by ID. */
  toggleSavedFlight(
    id: string,
    flightList: FlightList,
    flight: FlightListItem
  ): void {
    const savedFlights = [...this._savedFlights.getValue()];

    // Find flight and add or remove as needed.
    let foundIndex = -1;
    for (let i = 0; i < savedFlights.length; i++) {
      if (savedFlights[i].id === id) {
        foundIndex = i;
      }
    }

    if (foundIndex >= 0) {
      savedFlights.splice(foundIndex, 1);
    } else {
      savedFlights.push({
        id,
        depStation: flightList.depStation,
        arrStation: flightList.arrStation,
        depTime: flight.departureTime,
        arrTime: flight.arrivalTime,
        equipment: flight.equipment,
        flightNumber: flight.flightNumber,
        date: flightList.date.toISODate(),
      });

      // Lookup sequence information and store it in the saved flight data.
      this.getSequenceByFlight(
        flight.flightNumber,
        flightList.depStation,
        flightList.date.toISODate(),
        'CA'
      )
        .pipe(
          tap((requestState) => {
            if (requestState.hasError) {
              this.showErrorAlert(
                `Failed to find sequence for flight ${flight.flightNumber}.`
              );
            }

            if (requestState.response) {
              // Find flight within saved flights again, since it may have changed since this request was triggered.
              for (const savedFlight of savedFlights) {
                if (savedFlight.id === id) {
                  const seqRequest = requestState.response;

                  savedFlight.seqRequest = seqRequest;
                  savedFlight.flightUrl = this.createFlightUrl(
                    seqRequest.number,
                    'CA',
                    seqRequest.date,
                    savedFlight.flightNumber,
                    savedFlight.depStation,
                    savedFlight.arrStation,
                    savedFlight.date
                  );
                  this.setSavedFlights(savedFlights);
                }
              }
            }
          })
        )
        .subscribe();
    }

    this.setSavedFlights(savedFlights);
  }

  /** Removes saved/pinned flight from list. */
  removeSavedFlight(id: string): void {
    const savedFlights = [...this._savedFlights.getValue()];

    // Find flight and add or remove as needed.
    let foundIndex = -1;
    for (let i = 0; i < savedFlights.length; i++) {
      if (savedFlights[i].id === id) {
        foundIndex = i;
      }
    }

    if (foundIndex >= 0) {
      savedFlights.splice(foundIndex, 1);
      localStorage.setItem('savedFlights', JSON.stringify(savedFlights));
      this._savedFlights.next(savedFlights);
    }
  }

  getRouteResults(
    from: string,
    to: string,
    date: DateTime
  ): Observable<RequestState<FlightList>> {
    const cmd = `26A/${from}/${to}/${date.toFormat('ddLLL')}`;
    return this.decsService.submitDecsRequest({ cmd }).pipe(
      map((response) => {
        if (response.hasError) {
          this.showErrorAlert(`Failed to find flights for ${from}-${to}.`);
        }

        if (response.response) {
          const flightList = this.parseFlightList(
            response.response.result,
            from,
            to,
            date
          );
          return response.into<FlightList>(flightList);
        }
        return response.into<FlightList>();
      })
    );
  }

  parseFlightList(
    text: string,
    from: string,
    to: string,
    date: DateTime
  ): FlightList {
    const flightList: FlightList = {
      depStation: from,
      arrStation: to,
      date,
      flights: [],
    };

    const lines = text.split('\n');

    for (const line of lines) {
      if (line.length < this.EQUIP_COL[1]) {
        continue;
      }

      const flightNumber = line
        .substring(this.FLIGHT_COL[0], this.FLIGHT_COL[1])
        .trim();

      const departureTime = line.substring(
        this.DEP_TIME_COL[0],
        this.DEP_TIME_COL[1]
      );

      if (!/\d{4}/.test(departureTime)) {
        // If departure time isn't four digits, skip this line.
        continue;
      }

      departureTime.slice();

      const statusChar = line
        .substring(this.STATUS_COL[0], this.STATUS_COL[1])
        .trim();

      let status = 'scheduled';

      if (statusChar === 'E') {
        status = 'delayed';
      } else if (statusChar === '*') {
        status = 'departed';
      }

      const cxld = line.substring(this.CXLD_COL[0], this.CXLD_COL[1]).trim();
      if (cxld === 'CXLD') {
        status = 'canceled';
      }

      const arrivalTime = line.substring(
        this.ARR_TIME_COL[0],
        this.ARR_TIME_COL[1]
      );

      let equipment = line.substring(this.EQUIP_COL[0], this.EQUIP_COL[1]);
      if (equipment === '320') {
        equipment = '32F';
      }

      const id = `${flightNumber}${flightList.depStation}${
        flightList.arrStation
      }${flightList.date.toFormat('yyyy-MM-dd')}`;

      flightList.flights.push({
        id,
        flightNumber,
        departureTime,
        arrivalTime,
        equipment,
        status,
      });
    }

    return flightList;
  }

  /** Makes crew API call and returns sequence information for the given flight and seat. */
  getSequenceByFlight(
    flightNumber: string,
    depStation: string,
    date: string,
    seat: string = 'CA'
  ): Observable<RequestState<SequenceRequest>> {
    const crewRequest: CrewRequest = {
      flightNumber,
      depStation,
      date,
    };
    return this.crewService.getCrewList(crewRequest).pipe(
      map((requestState) => {
        if (!requestState.response) {
          return requestState.into<SequenceRequest>();
        }

        // Find matching seat
        for (const crew of requestState.response.flight) {
          if (seat === crew.seat) {
            const sequence: SequenceRequest = {
              number: crew.sequenceNumber || '',
              date: stripTime(crew.sequenceDate || ''),
              seat,
            };

            return requestState.into<SequenceRequest>(sequence);
          }
        }

        return requestState.into<SequenceRequest>();
      })
    );
  }

  /** Generates a flight URL for direct navigation. */
  private createFlightUrl(
    seqNum: string,
    seat: string,
    seqDate: string,
    flightNum: string,
    depStation: string,
    arrStation: string,
    flightDate: string
  ): string {
    // Flight URL format: sequence/{seq num}/{seat}/{seq start date yyyy-mm-dd}/flight/{flight num}/{dep station}/{arr station}/{dep date}
    return `sequence/${seqNum}/${seat}/${seqDate}/flight/${flightNum}/${depStation}/${arrStation}/${flightDate}`;
  }

  private showErrorAlert(message: string): void {
    this.alertService.setAlert({
      type: 'error',
      message,
    });
  }
}
